import React, { useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import FirstScreen from "./screens/FirstScreen";
import ChooseLanguageScreen from "./screens/ChooseLanguagesScreen/ChooseLanguageScreen";
import QuizzesScreen from "./screens/QuizzesScreen";
import BeforeQuizStartScreen from "./screens/BeforeQuizStartScreen/BeforeQuizStartScreen";
import QuizGame from "./screens/QuizGameScreen/QuizGameScreen";
import NotFound from "./screens/NotFound";
import { getAllAnswersImagesUrls } from "./helpers/dataHelpers";
import useSanityData from "./hooks/useSanityData";
import styles from "./App.module.css";

import AnswerPanelSoundFile from "./sounds/answer-panel.mp3";
import ButtonSoundFile from "./sounds/button.mp3";
import BuzzerStopTimerSoundFile from "./sounds/buzzer-stop-timer.mp3";
import ConfirmSoundFile from "./sounds/confirm.mp3";
import DownSoundFile from "./sounds/down.mp3";
import FallDownSoundFile from "./sounds/falldown.mp3";
import RightAnswerSoundFile from "./sounds/right-answer.mp3";
import TimerSoundFile from "./sounds/timer.mp3";
import UpSoundFile from "./sounds/up.mp3";
import winnerSoundFile from "./sounds/winner.mp3";
import WrongAnswerSoundFile from "./sounds/wrong-answer.mp3";
import useSound from "use-sound";

export const QuizStartScreenUrl = "/quiz-start";
export const ChooseLanguageScreenUrl = "/choose-language";
export const QuizzesScreenUrl = "/quizzes";
export const QuizGameScreenUrl = "/quiz-game";


function App() {
  const navigate = useNavigate();
  const { quizzes, loading } = useSanityData();
  // If Ctrl + Enter combination pressed - go to categories screen
  const keydownHandler = (e) => {
    if (e.key === "Enter" && e.ctrlKey) {
      localStorage.removeItem("activeQuizId");
      navigate(QuizzesScreenUrl);
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", keydownHandler);
    return () => window.removeEventListener("keydown", keydownHandler);
  }, []);

  // If Ctrl + Q combination pressed - go to start screen
  const keydownOutHandler = (e) => {
    if (e.key === "q" && e.ctrlKey) {
      navigate("/");
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", keydownOutHandler);
    return () => window.removeEventListener("keydown", keydownOutHandler);
  }, []);

  // cache images from sanity on load the application
  const cacheImages = async (imagesUrlArr) => {
    if (imagesUrlArr && imagesUrlArr.length) {
      const promises = await imagesUrlArr.map((src) => {
        return new Promise(function (resolve, reject) {
          const img = new Image();
          img.src = src;
          img.onload = resolve();
          img.onerror = reject();
        });
      });
      await Promise.all(promises);
    }
  };

  useEffect(() => {
    if (quizzes) {
      const imagesArr = getAllAnswersImagesUrls(quizzes);
      cacheImages(imagesArr);
    }
  }, [quizzes]);

  const [answerPanelSound] = useSound(AnswerPanelSoundFile);
  const [buttonSound] = useSound(ButtonSoundFile);
  const [buzzerStopTimerSound] = useSound(BuzzerStopTimerSoundFile);
  const [confirmSound] = useSound(ConfirmSoundFile);
  const [downSound] = useSound(DownSoundFile);
  const [fallDownSound] = useSound(FallDownSoundFile);
  const [rightAnswerSound, { stop: rightAnswerSoundStop }] =
    useSound(RightAnswerSoundFile);
  const [timerSound, { stop: timerSoundStop, pause: timerSoundPause }] =
    useSound(TimerSoundFile);
  const [upSound] = useSound(UpSoundFile);
  const [winnerSound, { stop: winnerSoundStop }] = useSound(winnerSoundFile);
  const [wrongAnswerSound, { stop: wrongAnswerSoundStop }] =
    useSound(WrongAnswerSoundFile);

  const sounds = {
    answerPanelSound,
    buttonSound,
    buzzerStopTimerSound,
    confirmSound,
    downSound,
    fallDownSound,
    rightAnswerSound,
    rightAnswerSoundStop,
    timerSound,
    timerSoundStop,
    timerSoundPause,
    upSound,
    winnerSound,
    winnerSoundStop,
    wrongAnswerSound,
    wrongAnswerSoundStop,
  };
  return (
    <>
      <div className={styles.main}>
        <div className={styles.container}>
          <Routes>
            <Route path="/" element={<FirstScreen />} />
            <Route
              path={ChooseLanguageScreenUrl}
              element={
                <ChooseLanguageScreen sounds={sounds} quizzes={quizzes} />
              }
            />
            <Route
              path={QuizzesScreenUrl}
              element={<QuizzesScreen quizzes={quizzes} sounds={sounds} />}
            />
            <Route
              path={QuizStartScreenUrl}
              element={
                <BeforeQuizStartScreen quizzes={quizzes} sounds={sounds} />
              }
            />
            <Route
              path={QuizGameScreenUrl}
              element={<QuizGame quizzes={quizzes} sounds={sounds} />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </div>
    </>
  );
}

export default App;
